import * as swcHelpers from '@swc/helpers';
import { END } from 'redux-saga';
import { takeLatest, fork, takeLeading, put, race, take, delay, call } from 'redux-saga/effects';
import { Util } from '@grandvisionhq/forms';
import { setPreferredShippingMethod } from '@grandvisionhq/shipping';
import { ShippingObjectTypes } from '../state.js';
import { TOGGLE_PREFILL, TOGGLE_PREFILL_FAILED, SELECT_SHIPPING_METHOD, togglePrefillSuccess, updateShippingDetails, UPDATE_SHIPPING_DETAILS, togglePrefillFailed, addressBookPrefillValidationFailed, TOGGLE_PREFILL_SUCCESS, addressBookPrefillValidationSuccess } from './actions.js';
import { PrefillSources } from './types.js';

const DEFAULT_STATE = {
    isValid: false,
    isEnabled: false,
    formData: {}
};
const DOB_OPTIONS = [
    'customerDateOfBirth',
    'customerDOB',
    'dateOfBirth'
];
const fieldsOverlapExists = (sourceFields, targetFields)=>sourceFields.filter((item)=>targetFields.includes(item)
    ).length
;
function* onTogglePrefill(action) {
    const { email , targetFormState , sourceFormName , targetFormName , source , addressId , validateForm ,  } = action.payload;
    if (!action.payload.data) {
        yield put(togglePrefillSuccess({
            source
        }));
        return;
    }
    const prefillData = action.payload.data[sourceFormName];
    const formizedData = Util.objectToFormState(action.payload.data);
    const checkoutForm = targetFormState || DEFAULT_STATE;
    const sourceForm = formizedData[sourceFormName];
    let newFieldValues = {};
    // When the checkoutForm contains an "email" field, we need to add it to the newFieldValues
    const checkoutFieldNames = Object.keys(checkoutForm.formData);
    const sourceFormFieldNames = sourceForm ? Object.keys(sourceForm === null || sourceForm === void 0 ? void 0 : sourceForm.formState) : [];
    if (sourceForm || checkoutFieldNames.includes('email')) {
        newFieldValues = checkoutFieldNames.reduce((fieldAcc, fieldName)=>{
            const checkoutFieldState = checkoutForm.formData[fieldName] || {
                name: fieldName,
                value: ''
            };
            let sourceFieldState = sourceForm === null || sourceForm === void 0 ? void 0 : sourceForm.formState[fieldName];
            /**
         *  Condition to handle case where key used to store
         *  dateOfBirth in personalDetails form is different than
         *  key used in customerDetails form of checkout
         */ if (DOB_OPTIONS.includes(fieldName) && fieldsOverlapExists(DOB_OPTIONS, sourceFormFieldNames) && !sourceFieldState) {
                sourceFieldState = (sourceForm === null || sourceForm === void 0 ? void 0 : sourceForm.formState.customerDateOfBirth) || (sourceForm === null || sourceForm === void 0 ? void 0 : sourceForm.formState.customerDOB) || (sourceForm === null || sourceForm === void 0 ? void 0 : sourceForm.formState.dateOfBirth);
            }
            const value = sourceFieldState && sourceFieldState.value ? sourceFieldState.value.toString() : checkoutFieldState.value;
            return swcHelpers.objectSpread({}, fieldAcc, {
                [fieldName]: swcHelpers.objectSpread({}, checkoutFieldState, {
                    value: fieldName === 'email' && email ? email : value,
                    isTouched: true
                })
            });
        }, {});
    } else {
        newFieldValues = checkoutForm.formData;
    }
    const shippingDetailsUpdate = {
        formName: targetFormName,
        formValid: targetFormState.isValid,
        formState: swcHelpers.objectSpread({}, checkoutForm.formData, newFieldValues)
    };
    try {
        /**
     * This action fills the form with the new updated data.
     */ yield put(updateShippingDetails({
            form: shippingDetailsUpdate,
            addressBookId: addressId
        }));
        const { updateShippingDetailsSuccess  } = yield race({
            updateShippingDetailsSuccess: take(UPDATE_SHIPPING_DETAILS),
            timeout: delay(10000)
        });
        /**
     * Wait for the UPDATE_SHIPPING_DETAILS action to be completed, before validating the updated form.
     */ if (updateShippingDetailsSuccess && validateForm) {
            yield call(validateForm, prefillData);
        }
        yield put(togglePrefillSuccess({
            source
        }));
    } catch (err) {
        yield put(togglePrefillFailed({
            source
        })) // for Redux logging purposes
        ;
    }
}
function* onTogglePrefillFailed(action) {
    const { reason  } = action.payload;
    if (action.payload.source === PrefillSources.AddressBook) {
        yield put(addressBookPrefillValidationFailed({
            reason: reason || 'error'
        }));
    }
}
function* listenForPrefill() {
    /**
   * After prefill of the address book data, the form that it gets prefilled into needs to
   * validate that data. Only after the (invisible) form has validated the data, we'll know
   * whether we want to show a summary (if all good) or a semi-prefilled form (if form is invalid).
   */ while(true){
        const result = yield take(TOGGLE_PREFILL_SUCCESS);
        if (result.payload.source === PrefillSources.AddressBook) {
            yield fork(handleAddressPrefill);
        }
    }
}
function* handleAddressPrefill() {
    while(true){
        const { updateShippingAction , timeout , end ,  } = yield race({
            updateShippingAction: take(UPDATE_SHIPPING_DETAILS),
            timeout: delay(10000),
            end: take(END.type)
        });
        if (end) {
            return;
        }
        if (timeout) {
            yield put(addressBookPrefillValidationFailed({
                reason: 'timeout'
            }));
            return;
        }
        if (updateShippingAction.payload.form.formName === ShippingObjectTypes.BillingAddress) {
            if (updateShippingAction.payload.form.formValid) {
                yield put(addressBookPrefillValidationSuccess());
            } else {
                yield put(addressBookPrefillValidationFailed({
                    reason: 'invalidData'
                }));
            }
            return;
        }
    }
}
// saga can be removed when setPreferredShippingMethod is moved to shipping package
function* onSelectShippingMethod(action) {
    const shippingMethod = action.payload.shippingMethod;
    if (shippingMethod.method) {
        // Store the last chosen shipping method as preferred method under shipping redux state key
        yield put(setPreferredShippingMethod({
            shippingMethodKey: shippingMethod.method
        }));
    }
}
var stepShippingSagas = [
    takeLatest(TOGGLE_PREFILL, onTogglePrefill),
    takeLatest(TOGGLE_PREFILL_FAILED, onTogglePrefillFailed),
    fork(listenForPrefill),
    takeLeading(SELECT_SHIPPING_METHOD, onSelectShippingMethod), 
];

export { stepShippingSagas as default, handleAddressPrefill, listenForPrefill, onSelectShippingMethod, onTogglePrefill, onTogglePrefillFailed };
//# sourceMappingURL=sagas.js.map
